import React, { useEffect, useState } from 'react';
import { useHeader } from '../../context/HeaderContext';
import { getCampaignCreationRequestDetails } from '../../api/campaign';
import { useNavigate, useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { MdArrowBack } from 'react-icons/md';

const NewCampaignRequestDetails = () => {
  const { setHeaderTitle } = useHeader();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState(null);

  useEffect(() => {
    setHeaderTitle('Campaign Request Details');
    if (id) {
      fetchRequestDetails();
    }
  }, [setHeaderTitle, id]);

  const fetchRequestDetails = async () => {
    try {
      setLoading(true);
      const data = await getCampaignCreationRequestDetails(id);
      setRequest(data);
    } catch (error) {
      console.error('Error fetching request details:', error);
      toast.error('Failed to load request details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'pending' || statusLower === 'active') {
      return <span className='px-4 py-2 bg-chipYellow text-white rounded-full text-base font-semibold'>Pending</span>;
    } else if (statusLower === 'approved' || statusLower === 'resolved') {
      return <span className='px-4 py-2 bg-chipGreen text-white rounded-full text-base font-semibold'>Approved</span>;
    } else if (statusLower === 'rejected') {
      return <span className='px-4 py-2 bg-chipRed text-white rounded-full text-base font-semibold'>Rejected</span>;
    }
    return <span className='px-4 py-2 bg-gray-400 text-white rounded-full text-base font-semibold'>{status || 'Pending'}</span>;
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center py-20'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-primary'></div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className='p-6'>
        <div className='text-center py-20'>
          <p className='text-lg text-gray-500'>Request not found</p>
          <button
            onClick={() => navigate('/campaigns/new-req')}
            className='mt-4 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primaryHoverDark'
          >
            Back to Requests
          </button>
        </div>
        <ToastContainer position="bottom-right" autoClose={2000} />
      </div>
    );
  }

  const campaignTypeLower = (request.campaignType || '').toLowerCase();
  const showBrandFields = campaignTypeLower.includes('brand') || campaignTypeLower.includes('product') || campaignTypeLower.includes('advertisement');
  const showAdvertisementFields = campaignTypeLower.includes('advertisement');
  const showChallengeFields = campaignTypeLower.includes('challenge');
  const showPlantTreesFields = campaignTypeLower.includes('plant') || campaignTypeLower.includes('tree');

  return (
    <div className='w-full'>
      <section className='bg-white rounded-md shadow-sm p-6'>
        <button
          onClick={() => navigate('/campaigns/new-req')}
          className='flex items-center gap-2 text-primary hover:text-primaryHoverDark mb-4 font-medium'
        >
          <MdArrowBack className='text-xl' />
          Back to Requests
        </button>

        {/* Header */}
        <div className='flex justify-between items-start mb-6 pb-6 border-b border-gray-200'>
          <div>
            <h1 className='text-2xl font-bold text-gray-900 mb-2'>Campaign Request Details</h1>
            <p className='text-sm text-gray-600'>Submitted on {formatDate(request.createdAt)}</p>
          </div>
          {getStatusBadge(request.status || (request.active ? 'Active' : 'Resolved'))}
        </div>

        {/* Request Details */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6'>
          <div>
            <label className='block text-sm font-medium text-gray-500 mb-1'>Campaign Type</label>
            <p className='text-base text-gray-900 font-semibold'>{request.campaignType || 'N/A'}</p>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-500 mb-1'>Campaign Title</label>
            <p className='text-base text-gray-900 font-semibold'>{request.campaignTitle || 'N/A'}</p>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-500 mb-1'>Start Date</label>
            <p className='text-base text-gray-900'>{formatDate(request.startDate)}</p>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-500 mb-1'>End Date</label>
            <p className='text-base text-gray-900'>{formatDate(request.endDate)}</p>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-500 mb-1'>Campaign Budget</label>
            <p className='text-base text-gray-900'>{formatCurrency(request.campaignBudget)}</p>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-500 mb-1'>Duration</label>
            <p className='text-base text-gray-900'>
              {request.startDate && request.endDate
                ? `${Math.ceil((new Date(request.endDate) - new Date(request.startDate)) / (1000 * 60 * 60 * 24))} days`
                : 'N/A'}
            </p>
          </div>
        </div>

        {/* Campaign-Specific Fields */}
        {showBrandFields && (request.landingPageUrl || request.ctaButtonText) && (
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 pt-6 border-t border-gray-200'>
            <div className='md:col-span-2'>
              <h3 className='text-lg font-bold text-gray-900 mb-4'>Brand Promotion Details</h3>
            </div>

            {request.landingPageUrl && (
              <div>
                <label className='block text-sm font-medium text-gray-500 mb-1'>Landing Page URL</label>
                <a
                  href={request.landingPageUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-base text-primary hover:underline'
                >
                  {request.landingPageUrl}
                </a>
              </div>
            )}

            {request.ctaButtonText && (
              <div>
                <label className='block text-sm font-medium text-gray-500 mb-1'>CTA Button Text</label>
                <p className='text-base text-gray-900'>{request.ctaButtonText}</p>
              </div>
            )}
          </div>
        )}

        {showAdvertisementFields && (request.advertisementType || request.advertisementDetails) && (
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 pt-6 border-t border-gray-200'>
            <div className='md:col-span-2'>
              <h3 className='text-lg font-bold text-gray-900 mb-4'>Advertisement Details</h3>
            </div>

            {request.advertisementType && (
              <div>
                <label className='block text-sm font-medium text-gray-500 mb-1'>Advertisement Type</label>
                <p className='text-base text-gray-900'>{request.advertisementType}</p>
              </div>
            )}

            {request.advertisementDetails && (
              <div className='md:col-span-2'>
                <label className='block text-sm font-medium text-gray-500 mb-1'>Advertisement Details</label>
                <p className='text-base text-gray-900 whitespace-pre-wrap'>{request.advertisementDetails}</p>
              </div>
            )}
          </div>
        )}

        {showChallengeFields && (request.challengeCategory || request.rewardsOffered) && (
          <div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 pt-6 border-t border-gray-200'>
            <div className='md:col-span-2'>
              <h3 className='text-lg font-bold text-gray-900 mb-4'>Challenge Details</h3>
            </div>

            {request.challengeCategory && (
              <div>
                <label className='block text-sm font-medium text-gray-500 mb-1'>Challenge Category</label>
                <p className='text-base text-gray-900'>{request.challengeCategory}</p>
              </div>
            )}

            {request.rewardsOffered && (
              <div>
                <label className='block text-sm font-medium text-gray-500 mb-1'>Rewards Offered</label>
                <p className='text-base text-gray-900'>{request.rewardsOffered}</p>
              </div>
            )}
          </div>
        )}

        {showPlantTreesFields && request.sponsorMessage && (
          <div className='grid grid-cols-1 gap-6 mb-6 pt-6 border-t border-gray-200'>
            <div>
              <h3 className='text-lg font-bold text-gray-900 mb-4'>Plant Trees Details</h3>
            </div>

            <div>
              <label className='block text-sm font-medium text-gray-500 mb-1'>Sponsor Message</label>
              <p className='text-base text-gray-900 whitespace-pre-wrap'>{request.sponsorMessage}</p>
            </div>
          </div>
        )}

        {/* Description */}
        {(request.message || request.description) && (
          <div className='pt-6 border-t border-gray-200'>
            <label className='block text-sm font-medium text-gray-500 mb-2'>Description</label>
            <div
              className='text-base text-gray-900 prose max-w-none'
              dangerouslySetInnerHTML={{ __html: request.message || request.description }}
            />
          </div>
        )}

        {/* Uploaded Image */}
        {request.image && (
          <div className='pt-6 border-t border-gray-200'>
            <label className='block text-sm font-medium text-gray-500 mb-3'>Uploaded Image</label>
            <div className='bg-gray-50 rounded-lg p-4 border border-gray-200'>
              <img
                src={request.image}
                alt="Campaign Request Attachment"
                className='max-w-full h-auto rounded-lg shadow-sm cursor-pointer hover:opacity-90 transition-opacity'
                style={{ maxHeight: '400px', objectFit: 'contain' }}
                onClick={() => window.open(request.image, '_blank')}
              />
              <p className='text-xs text-gray-500 mt-2'>Click image to view full size</p>
            </div>
          </div>
        )}

        {/* Admin Response */}
        {request.adminResponse && (
          <div className='mt-6 pt-6 border-t border-gray-200'>
            <label className='block text-sm font-medium text-gray-500 mb-2'>Admin Response</label>
            <div className='bg-gray-50 rounded-md p-4'>
              <p className='text-base text-gray-900'>{request.adminResponse}</p>
              {request.respondedAt && (
                <p className='text-sm text-gray-600 mt-2'>Responded on {formatDate(request.respondedAt)}</p>
              )}
            </div>
          </div>
        )}
      </section>

      <ToastContainer position="bottom-right" autoClose={2000} />
    </div>
  );
};

export default NewCampaignRequestDetails;
